<template>
  <div :class="[$style.container, compact && $style.containerCompact]">
    <apexchart
      :width="width"
      :height="height"
      type="donut"
      :options="chartOptions"
      :series="series"
    ></apexchart>
    <div v-if="title" :class="$style.title">{{ title }}</div>
  </div>
</template>

<script setup lang="ts">
import type { ApexOptions } from 'apexcharts';
import { reactive, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    labels: string[];
    series: number[];
    gradientColorsFrom: string[];
    gradientColorsTo: string[];
    title?: string;
    height?: number | string;
    width?: number | string;
    disableResponsive?: boolean;
    compact?: boolean;
  }>(),
  {
    title: '',
    height: 210,
    width: '100%',
    disableResponsive: false,
    compact: false,
  },
);

const defaultResponsive: ApexOptions['responsive'] = [
  {
    breakpoint: 480,
    options: {
      chart: {
        width: 200,
      },
      legend: {
        position: 'bottom',
      },
    },
  },
];

const chartOptions = reactive<ApexOptions>({
  title: {
    text: '',
  },
  colors: props.gradientColorsFrom,
  dataLabels: {
    enabled: false,
  },
  states: {
    hover: {
      filter: {
        type: 'none', // Disable any changes on hover
      },
    },
    active: {
      filter: {
        type: 'none', // Disable any changes on click
      },
    },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'light',
      shadeIntensity: 0.5,
      gradientToColors: props.gradientColorsTo,
      opacityFrom: 0.7,
      opacityTo: 1,
      type: 'diagonal1',
      stops: [0, 100],
    },
  },
  legend: {
    show: false,
  },
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  labels: props.labels,
  responsive: props.disableResponsive ? [] : defaultResponsive,
});

defineExpose({ chartOptions });

watch(
  () => props.disableResponsive,
  (disabled) => {
    chartOptions.responsive = disabled ? [] : defaultResponsive;
  },
);

watch(
  () => props.labels,
  (newLabels: string[]) => {
    chartOptions.labels = newLabels;
  },
);
</script>

<style lang="scss" module>
@import '@/assets/variables';

.container {
  width: 100%;
  padding-top: 0;
  padding-bottom: $space-100;
}

.containerCompact {
  padding-bottom: 0;
}

.title {
  margin-top: 0;
  margin-bottom: $space-50;
  text-align: center;
}
</style>
